import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { addMonths, format } from 'date-fns';
import { Box, Text } from 'grommet';
import { FormAdd, FormSubtract } from 'grommet-icons';
import { useEffect, useState } from 'preact/hooks';

import { OutlineButton, PrimaryButton } from '@/components/Buttons';
import { getCurrencySymbol } from '@/components/products/utils';
import { getUserProductsQueryOptions, postUserRetentionOfferMutationFn } from '@/queries/products';
import { ParsedUserProduct } from '@/queries/products/types';
import { setUserData } from '@/redux/slices/user/actions';
import store from '@/redux/store';
import { buildURL, RequestError } from '@/shared/fetch';
import { trackMP } from '@/shared/mp';

const offerTexts = {
  percentageOff: {
    title: "Here's %X%% off for the next %Y% %Z%",
    description:
      'We are a small team hoping to connect the world through animals and are trying our hardest to build the best product possible.\n\nYour memberships helps to keep us going and receive funding support from our partners to continue building for a better product experience.\n\nWould you care to be patient and bear with us if we gave you %X%% off for the next %Y% %Z%?',
    mpEventName: 'percentage-discount',
  },
  pauseSubscription: {
    title: 'Pause your membership',
    description:
      "We understand that you don't use zoolife right now, but we'd hate to lose you as a member.\n\nWould you consider pausing for a couple of months and retain your membership status during your pause?",
    mpEventName: 'subscription-pause',
  },
  oneMonthOnUs: {
    title: 'Get one month for free on us',
    description:
      'Thank you for notifying us of your technical issues. We are a small team and support is on its way. We will try our hardest to resolve this issue for you. While you bear with us, we would like to offer you 1 month of access for free.',
    mpEventName: 'invoice-item-credit',
  },
};

const offerDetails = {
  month: { percentOff: 50, durationInMonths: 3 },
  year: { percentOff: 25, durationInYear: 1 },
};

const isMonthlyProduct = (productLookupKey: string | undefined) => {
  return productLookupKey === 'monthly-member' || productLookupKey === 'monthly-educator';
};

const QuantitySelector = ({
  value,
  onChange,
  min,
  max,
}: {
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
}) => {
  const handleDecrease = () => {
    if (value > min) onChange(value - 1);
  };

  const handleIncrease = () => {
    if (value < max) onChange(value + 1);
  };

  return (
    <div className="inline-flex items-center overflow-hidden rounded-full border border-stone-300">
      <button
        type="button"
        onClick={handleDecrease}
        disabled={value <= min}
        className="preflight preflight-button flex items-center justify-center p-2 transition-colors hover:bg-stone-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <FormSubtract size="small" />
      </button>
      <span className="flex min-w-[60px] items-baseline justify-center whitespace-nowrap px-1.5 text-base">
        {value} <span className="ml-1.5 text-xs">month{value !== 1 ? 's' : ''}</span>
      </span>
      <button
        type="button"
        onClick={handleIncrease}
        disabled={value >= max}
        className="preflight preflight-button flex items-center justify-center p-2 transition-colors hover:bg-stone-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <FormAdd size="small" />
      </button>
    </div>
  );
};

type OfferType = 'percentageOff' | 'pauseSubscription' | 'oneMonthOnUs';

interface CancellationFlowOfferProps {
  userProduct: ParsedUserProduct;
  offerType: OfferType;
  /** The reason the user gave for cancelling, sent with the offer so it is recorded either way. */
  reason: string;
  productLookupKey: string | undefined;
  onNoThanks: () => void;
  onAfterClaimOfferClose: () => void;
}

const refreshUserData = (queryClient: QueryClient) => async () => {
  queryClient.invalidateQueries({ queryKey: getUserProductsQueryOptions().queryKey });
  try {
    const res = await fetch(buildURL('/users/user'));
    const user = await res.json();
    store.dispatch(setUserData(user));
  } catch (error) {
    console.error(error);
  }
};

const CancellationFlowOffer = ({
  userProduct,
  offerType,
  reason,
  productLookupKey,
  onNoThanks,
  onAfterClaimOfferClose,
}: CancellationFlowOfferProps) => {
  const [pauseDuration, setPauseDuration] = useState(1);
  const queryClient = useQueryClient();
  const { title, description } = offerTexts[offerType];
  const isMonthly = isMonthlyProduct(productLookupKey);

  const noThanksHandler = () => {
    trackMP(`${offerTexts[offerType].mpEventName}-declined-by-user`);
    onNoThanks();
  };

  useEffect(() => {
    trackMP(`${offerTexts[offerType].mpEventName}-presented-to-user`);
  }, [offerType]);

  const {
    mutate,
    data: apiResponse,
    error,
    isSuccess: isOfferClaimed,
    isPending,
  } = useMutation({
    mutationFn: postUserRetentionOfferMutationFn,
    onSuccess: refreshUserData(queryClient),
    onError: (error) => {
      const message = error instanceof RequestError ? error.body?.error : 'An error occurred while claiming the offer.';
      trackMP(`${offerTexts[offerType].mpEventName}-error`, { error: message });
    },
  });

  const apiError = !error
    ? null
    : error instanceof RequestError
    ? error.body?.error
    : 'An error occurred while claiming the offer.';

  const handleClaimOffer = async () => {
    mutate({ userProductId: userProduct._id, offerType, pauseDuration, reason });
  };

  const replacePlaceholders = (text: string) => {
    const offerDetail = isMonthly ? offerDetails.month : offerDetails.year;
    const { percentOff } = offerDetail;
    const duration = isMonthly ? offerDetails.month.durationInMonths : offerDetails.year.durationInYear;
    const unit = isMonthly ? 'months' : 'year';
    return text.replace(/%X%/g, String(percentOff)).replace(/%Y%/g, String(duration)).replace(/%Z%/g, unit);
  };

  const renderDescription = (text: string) => {
    const replacedText = replacePlaceholders(text);
    return replacedText.split('\n').map((line, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <span key={index.toString()}>
        {line}
        {index < replacedText.split('\n').length - 1 && <br />}
      </span>
    ));
  };

  const renderPauseDurationInput = () => {
    if (offerType !== 'pauseSubscription') return null;
    return (
      <Box
        direction="row"
        align="center"
        justify="between"
        margin={{ vertical: 'medium' }}
        className="mb-6 flex items-center justify-between"
      >
        <Text className="mr-4 text-sm text-stone-600">Select pause duration:</Text>
        <QuantitySelector
          value={pauseDuration}
          onChange={setPauseDuration}
          min={apiResponse ? pauseDuration : 1}
          max={apiResponse ? pauseDuration : 6}
        />
      </Box>
    );
  };

  const renderSuccessMessage = () => {
    if (!apiResponse) return null;

    if (offerType === 'percentageOff') {
      const discountDetails = apiResponse.discountDetails as {
        percentOff?: number;
        durationInMonths?: number;
      };
      const upcomingInvoice = apiResponse.upcomingInvoice as { date?: string; amount?: number; currency?: string };
      const formattedDate = format(new Date(upcomingInvoice?.date ?? Date.now()), 'dd MMM yyyy');
      const currencySymbol = getCurrencySymbol(upcomingInvoice?.currency);
      const isYearly = discountDetails?.durationInMonths === 12;

      return (
        <p className="mb-4 text-left font-medium text-green-600">
          ✔{' '}
          {`Offer successfully claimed! You've received a ${discountDetails?.percentOff}% discount
          for ${isYearly ? '1 year' : `${discountDetails?.durationInMonths} months`}.
          You will be charged only ${currencySymbol}${upcomingInvoice?.amount} for the
          ${isYearly ? 'next year' : `next ${discountDetails?.durationInMonths} months`},
          starting your next billing cycle on ${formattedDate}.`}
        </p>
      );
    }

    if (offerType === 'pauseSubscription') {
      const validUntil = new Date(userProduct.validUntil);
      const resumeDate = addMonths(validUntil, pauseDuration);
      return (
        <p className="mb-4 text-left font-medium text-green-600">
          ✔{' '}
          {`Your subscription has been paused successfully. Your current subscription will be valid until ${format(
            validUntil,
            'dd MMM yyyy',
          )}. After that, it will be paused for ${pauseDuration} month${
            pauseDuration > 1 ? 's' : ''
          }. Your subscription will resume on ${format(resumeDate, 'dd MMM yyyy')}.`}
        </p>
      );
    }

    if (offerType === 'oneMonthOnUs') {
      const validUntil = new Date(userProduct.validUntil);
      const formattedDate = format(validUntil, 'dd MMM yyyy');
      const currencySymbol = getCurrencySymbol(userProduct.currency);

      return (
        <p className="mb-4 text-left font-medium text-green-600">
          ✔{' '}
          {`Great news! You've received one month free. You will be charged ${currencySymbol}0 on your next billing cycle, ${formattedDate}.`}
        </p>
      );
    }

    return null;
  };

  return (
    <div className="flex flex-col items-center px-10 pb-10 pt-5 text-center mobile:px-5">
      <h4 className="mb-4 mt-6 text-[30px] font-bold leading-none text-[var(--primaryGreen)] mobile:mb-3 mobile:mt-5 mobile:text-[26px]">
        {replacePlaceholders(title)}
      </h4>
      <p className="mb-6 w-full whitespace-pre-line text-left text-base font-light text-black mobile:mb-5">
        {renderDescription(description)}
      </p>
      {renderPauseDurationInput()}
      {isOfferClaimed ? (
        <>
          {renderSuccessMessage()}
          <PrimaryButton label="Close" onClick={onAfterClaimOfferClose} className="w-full max-w-[200px]" />
        </>
      ) : (
        <div className="mt-5 flex w-full justify-center gap-4 pb-3 mobile:flex-col mobile:gap-3">
          <OutlineButton
            label={apiError ? 'Close' : 'No thanks'}
            onClick={noThanksHandler}
            className="flex-1 mobile:max-w-full"
          />
          <PrimaryButton
            label="Claim offer"
            onClick={handleClaimOffer}
            className="flex-1 mobile:max-w-full"
            disabled={isOfferClaimed || isPending}
            loading={isPending}
          />
        </div>
      )}
      {apiError && <p className="mb-4 text-center text-red-500">{apiError}</p>}
    </div>
  );
};

export default CancellationFlowOffer;
